/**
 * Money formatting — convert minor units (int) → display string,
 * and parse user input back to minor units.
 *
 * All Finance storage is BIGINT minor units (cents). Floats never
 * touch the DB.
 */

/** "KSh 125,430" */
export function formatMoney(minor: number, currencyCode = 'KES'): string {
  const major = minor / 100;
  const prefix = currencyCode === 'KES' ? 'KSh' : currencyCode;
  const formatted = new Intl.NumberFormat('en-KE', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.round(major));
  return `${prefix} ${formatted}`;
}

/** Compact for tight UI: "KSh 1.2k", "KSh 125k", "KSh 1.4M" */
export function formatMoneyCompact(minor: number, currencyCode = 'KES'): string {
  const major = Math.abs(minor / 100);
  const sign = minor < 0 ? '−' : '';
  const prefix = currencyCode === 'KES' ? 'KSh' : currencyCode;

  if (major >= 1_000_000) {
    return `${sign}${prefix} ${(major / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  }
  if (major >= 1_000) {
    return `${sign}${prefix} ${(major / 1_000).toFixed(major >= 10_000 ? 0 : 1).replace(/\.0$/, '')}k`;
  }
  return `${sign}${prefix} ${Math.round(major)}`;
}

/**
 * Parse user-typed money string → minor units (integer).
 * Accepts: "1200", "1,200", "1200.50", "1 200.50", "KSh 1,200"
 * Returns null if unparseable.
 * KES is treated as a 0-decimal currency (rounded), others keep 2.
 */
export function parseMoneyToMinor(raw: string, currencyCode = 'KES'): number | null {
  if (typeof raw !== 'string') return null;
  const cleaned = raw
    .replace(/[,\s]/g, '')
    .replace(/^[A-Za-z$€£¥₹]+/, '')
    .trim();
  if (!cleaned) return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n) || n < 0) return null;
  if (currencyCode === 'KES') {
    return Math.round(n) * 100;
  }
  return Math.round(n * 100);
}
